import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { googleSession } from '../../src/api/auth';

export default function GoogleAuthCallback({ onSuccess }) {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const hasResolvedRef = useRef(false);

  useEffect(() => {
    if (hasResolvedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      setError('Missing login token.');
      return;
    }

    const run = async () => {
      try {
        const { data } = await googleSession(token);
        if (hasResolvedRef.current) return;

        if (data?.requiresOnboarding) {
          const setupToken = data?.onboarding?.token || token;
          hasResolvedRef.current = true;
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({ type: 'google-auth-setup', token: setupToken }, window.location.origin);
            window.close();
            return;
          }
          navigate(`/register?googleSetupToken=${encodeURIComponent(setupToken)}`);
          return;
        }

        hasResolvedRef.current = true;
        const resolvedToken = data?.token || token;
        const authPayload = {
          user: data?.user,
          token: resolvedToken,
          rememberMe: true,
          expiresAt: data?.expiresAt ?? null,
        };

        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ type: 'google-auth', ...authPayload }, window.location.origin);
          window.close();
          return;
        }
        onSuccess(authPayload);
        navigate('/dashboard');
      } catch (err) {
        if (hasResolvedRef.current) return;
        const message = err.response?.data?.message || 'Google login failed. Please try again.';
        if (window.opener && !window.opener.closed) {
          hasResolvedRef.current = true;
          window.opener.postMessage({ type: 'google-auth-error', message }, window.location.origin);
          window.close();
          return;
        }
        setError(message);
      }
    };

    run();
  }, [navigate, onSuccess]);

  return (
    <div className="auth-page">
      <main className="auth-page__body">
        <div className="auth-page__inner">
          <section className="auth-page__form">
            <div className="auth-card">
              <div className="auth-card__header">
                <h2>Completing Google sign-in</h2>
                <p>{error || 'Please wait while we finish signing you in.'}</p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

GoogleAuthCallback.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};
